import { ReactRenderer } from "@tiptap/react";
import type { SuggestionOptions, SuggestionProps } from "@tiptap/suggestion";
import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { DynamicNodePopover } from "../dynamic-node-popover";
import type { SlashDataKeyItem } from "./data-key-registry";
import {
  SlashCommandList,
  type SlashCommandListProps,
  type SlashCommandListRef,
} from "./slash-command-list";

const POPUP_CLASS = "tiptap-slash-command-popup";

interface SlashCommandPopupProps extends SlashCommandListProps {
  anchorRect: DOMRect | null | undefined;
}

const SlashCommandPopup = forwardRef<
  SlashCommandListRef,
  SlashCommandPopupProps
>(({ anchorRect, items, command }, ref) => {
  const listRef = useRef<SlashCommandListRef | null>(null);
  const anchorEl = useMemo(
    () => (anchorRect ? { getBoundingClientRect: () => anchorRect } : null),
    [anchorRect]
  );

  useImperativeHandle(ref, () => ({
    onKeyDown: (event) => listRef.current?.onKeyDown(event) ?? false,
  }));

  if (!anchorEl) {
    return null;
  }

  return (
    <DynamicNodePopover anchorEl={anchorEl} className={POPUP_CLASS}>
      <SlashCommandList ref={listRef} items={items} command={command} />
    </DynamicNodePopover>
  );
});

SlashCommandPopup.displayName = "SlashCommandPopup";

const buildProps = (
  props: SuggestionProps<SlashDataKeyItem, SlashDataKeyItem>
): SlashCommandPopupProps => {
  return {
    anchorRect: props.clientRect?.(),
    items: props.items,
    command: (item) => props.command(item),
  };
};

/**
 * Suggestion `render` lifecycle for the `/` slash command: mounts the React list
 * in the shared viewport-aware popover and forwards keyboard events to the
 * list's imperative handle.
 */
export const renderSlashCommandPopup: SuggestionOptions<
  SlashDataKeyItem,
  SlashDataKeyItem
>["render"] = () => {
  let component: ReactRenderer<
    SlashCommandListRef,
    SlashCommandPopupProps
  > | null = null;

  return {
    onStart: (props) => {
      component = new ReactRenderer(SlashCommandPopup, {
        props: buildProps(props),
        editor: props.editor,
      });
      document.body.appendChild(component.element);
    },

    onUpdate: (props) => {
      component?.updateProps(buildProps(props));
    },

    onKeyDown: (props) => {
      if (props.event.key === "Escape") {
        return false;
      }
      return component?.ref?.onKeyDown(props.event) ?? false;
    },

    onExit: () => {
      component?.element.remove();
      component?.destroy();
      component = null;
    },
  };
};
